import {
  PROTOCOL_LIMITS,
  PROTOCOL_VERSION,
  ROOM_DURATION_LIMITS,
  type AnyProtocolMessage,
  type IceCandidatePayload,
  type IceServerPayload,
  type PeerRole,
  type ProtocolMessageType,
  type RoomCredential,
} from './messages';

const MESSAGE_TYPES: ReadonlySet<string> = new Set<ProtocolMessageType>([
  'room.create',
  'room.created',
  'room.join',
  'room.joined',
  'room.lock',
  'room.unlock',
  'room.error',
  'peer.offer',
  'peer.answer',
  'peer.ice',
  'peer.ready',
  'peer.leave',
  'host.kick',
  'turn.refresh',
  'turn.credentials',
  'heartbeat',
  'error',
]);

const ROOM_ERROR_CODES = new Set([
  'ROOM_NOT_FOUND',
  'ROOM_EXPIRED',
  'ROOM_LOCKED',
  'WRONG_PIN',
  'ROOM_FULL',
  'RATE_LIMITED',
  'VIEWER_REMOVED',
]);

const PROTOCOL_ERROR_CODES = new Set([
  'INVALID_MESSAGE',
  'MESSAGE_TOO_LARGE',
  'UNSUPPORTED_VERSION',
  'AUTH_REQUIRED',
  'UNAUTHORIZED',
  'ROLE_FORBIDDEN',
  'INTERNAL_ERROR',
]);

const LEAVE_REASONS = new Set(['left', 'kicked', 'host-ended', 'session-expired']);
const MESSAGE_ID = /^[A-Za-z0-9_-]{1,64}$/;
const TOKEN = /^[A-Za-z0-9_-]{32,128}$/;
const ROOM_CODE = /^\d{6}$/;
const PIN = /^\d{4}$/;

export interface ProtocolEnvelope {
  readonly v: typeof PROTOCOL_VERSION;
  readonly type: ProtocolMessageType;
  readonly id: string;
  readonly epoch: number;
  readonly payload: unknown;
}

export type ProtocolParseResult =
  | { readonly ok: true; readonly message: AnyProtocolMessage }
  | { readonly ok: false; readonly reason: 'invalid' | 'too-large' | 'unsupported-version' };

export function parseProtocolMessage(input: string | unknown): ProtocolParseResult {
  if (typeof input === 'string' && input.length > PROTOCOL_LIMITS.messageCharacters) {
    return { ok: false, reason: 'too-large' };
  }

  let value: unknown = input;
  if (typeof input === 'string') {
    try {
      value = JSON.parse(input) as unknown;
    } catch {
      return { ok: false, reason: 'invalid' };
    }
  }

  if (isRecord(value) && value.v !== PROTOCOL_VERSION) {
    return { ok: false, reason: 'unsupported-version' };
  }

  if (!isProtocolEnvelope(value) || !isValidPayload(value.type, value.payload)) {
    return { ok: false, reason: 'invalid' };
  }

  return { ok: true, message: value as AnyProtocolMessage };
}

export function isProtocolEnvelope(value: unknown): value is ProtocolEnvelope {
  if (!isExactRecord(value, ['v', 'type', 'id', 'epoch', 'payload'])) {
    return false;
  }

  return (
    value.v === PROTOCOL_VERSION &&
    typeof value.type === 'string' &&
    MESSAGE_TYPES.has(value.type) &&
    typeof value.id === 'string' &&
    MESSAGE_ID.test(value.id) &&
    Number.isSafeInteger(value.epoch) &&
    Number(value.epoch) >= 0
  );
}

function isValidPayload(type: ProtocolMessageType, payload: unknown): boolean {
  switch (type) {
    case 'room.create':
      return (
        isExactRecord(payload, ['durationSeconds']) &&
        (payload.durationSeconds === null ||
          (isNonNegativeInteger(payload.durationSeconds) &&
            Number(payload.durationSeconds) >= ROOM_DURATION_LIMITS.minimumSeconds &&
            Number(payload.durationSeconds) <= ROOM_DURATION_LIMITS.maximumSeconds))
      );
    case 'room.lock':
    case 'room.unlock':
    case 'host.kick':
    case 'turn.refresh':
      return isExactRecord(payload, []);
    case 'room.created':
      return (
        isExactRecord(payload, ['roomCode', 'pin', 'inviteUrl', 'hostToken', 'expiresAt']) &&
        isRoomCode(payload.roomCode) &&
        typeof payload.pin === 'string' &&
        PIN.test(payload.pin) &&
        isBoundedString(payload.inviteUrl, 2_048) &&
        isToken(payload.hostToken) &&
        (payload.expiresAt === null || isIsoDate(payload.expiresAt))
      );
    case 'room.join':
      return (
        isExactRecord(payload, ['roomCode', 'role', 'credential']) &&
        isRoomCode(payload.roomCode) &&
        isRole(payload.role) &&
        isCredentialForRole(payload.credential, payload.role)
      );
    case 'room.joined':
      return (
        isExactRecord(payload, [
          'role',
          'expiresAt',
          'resumeToken',
          'reconnected',
          'locked',
          'iceServers',
          'iceServersExpiresAt',
        ]) &&
        isRole(payload.role) &&
        (payload.expiresAt === null || isIsoDate(payload.expiresAt)) &&
        isToken(payload.resumeToken) &&
        typeof payload.reconnected === 'boolean' &&
        typeof payload.locked === 'boolean' &&
        isIceServers(payload.iceServers) &&
        (payload.iceServersExpiresAt === null || isIsoDate(payload.iceServersExpiresAt))
      );
    case 'turn.credentials':
      return (
        isExactRecord(payload, ['iceServers', 'expiresAt']) &&
        isIceServers(payload.iceServers) &&
        (payload.expiresAt === null || isIsoDate(payload.expiresAt))
      );
    case 'room.error':
      return (
        isRecordWithOptionalKeys(payload, ['code'], ['retryAfterMs']) &&
        typeof payload.code === 'string' &&
        ROOM_ERROR_CODES.has(payload.code) &&
        (payload.retryAfterMs === undefined || isNonNegativeInteger(payload.retryAfterMs))
      );
    case 'peer.offer':
    case 'peer.answer':
      return (
        isExactRecord(payload, ['negotiationId', 'sdp']) &&
        isBoundedString(payload.negotiationId, PROTOCOL_LIMITS.negotiationIdCharacters) &&
        isBoundedString(payload.sdp, PROTOCOL_LIMITS.sdpCharacters)
      );
    case 'peer.ice':
      return (
        isExactRecord(payload, ['negotiationId', 'candidate']) &&
        isBoundedString(payload.negotiationId, PROTOCOL_LIMITS.negotiationIdCharacters) &&
        (payload.candidate === null || isIceCandidate(payload.candidate))
      );
    case 'peer.ready':
      return isExactRecord(payload, ['role']) && isRole(payload.role);
    case 'peer.leave':
      return (
        isExactRecord(payload, ['reason']) &&
        typeof payload.reason === 'string' &&
        LEAVE_REASONS.has(payload.reason)
      );
    case 'heartbeat':
      return isExactRecord(payload, ['sentAt']) && isNonNegativeInteger(payload.sentAt);
    case 'error':
      return (
        isExactRecord(payload, ['code']) &&
        typeof payload.code === 'string' &&
        PROTOCOL_ERROR_CODES.has(payload.code)
      );
  }
}

function isIceServers(value: unknown): value is readonly IceServerPayload[] {
  return (
    Array.isArray(value) &&
    value.length <= PROTOCOL_LIMITS.iceServers &&
    value.every((server) => {
      if (!isRecordWithOptionalKeys(server, ['urls'], ['username', 'credential'])) return false;
      if (
        !Array.isArray(server.urls) ||
        server.urls.length === 0 ||
        server.urls.length > PROTOCOL_LIMITS.iceServerUrls ||
        !server.urls.every(isIceServerUrl)
      ) {
        return false;
      }
      const hasUsername = typeof server.username === 'string';
      const hasCredential = typeof server.credential === 'string';
      return (
        hasUsername === hasCredential &&
        (!hasUsername ||
          (isBoundedString(server.username, PROTOCOL_LIMITS.iceServerCredentialCharacters) &&
            isBoundedString(server.credential, PROTOCOL_LIMITS.iceServerCredentialCharacters)))
      );
    })
  );
}

function isIceServerUrl(value: unknown): value is string {
  return (
    isBoundedString(value, PROTOCOL_LIMITS.iceServerUrlCharacters) &&
    /^(?:stun|stuns|turn|turns):[^\s]+$/iu.test(value)
  );
}

function isCredentialForRole(value: unknown, role: PeerRole): value is RoomCredential {
  if (
    !isExactRecord(value, ['type', 'value']) ||
    typeof value.type !== 'string' ||
    typeof value.value !== 'string'
  ) {
    return false;
  }

  if (value.type === 'resume-token') {
    return TOKEN.test(value.value);
  }

  if (role === 'host') {
    return value.type === 'host-token' && TOKEN.test(value.value);
  }

  return (
    (value.type === 'pin' && PIN.test(value.value)) ||
    (value.type === 'invite-token' && TOKEN.test(value.value))
  );
}

function isIceCandidate(value: unknown): value is IceCandidatePayload {
  if (
    !isRecordWithOptionalKeys(
      value,
      ['candidate', 'sdpMLineIndex', 'sdpMid'],
      ['usernameFragment'],
    ) ||
    !isBoundedString(value.candidate, PROTOCOL_LIMITS.iceCandidateCharacters) ||
    !(value.sdpMLineIndex === null || isNonNegativeInteger(value.sdpMLineIndex)) ||
    !(value.sdpMid === null || isBoundedString(value.sdpMid, 256))
  ) {
    return false;
  }

  return (
    value.usernameFragment === undefined ||
    value.usernameFragment === null ||
    isBoundedString(value.usernameFragment, 256)
  );
}

function isRole(value: unknown): value is PeerRole {
  return value === 'host' || value === 'viewer';
}

function isRoomCode(value: unknown): value is string {
  return typeof value === 'string' && ROOM_CODE.test(value);
}

function isToken(value: unknown): value is string {
  return typeof value === 'string' && TOKEN.test(value);
}

function isIsoDate(value: unknown): value is string {
  return typeof value === 'string' && value.length <= 32 && !Number.isNaN(Date.parse(value));
}

function isBoundedString(value: unknown, maximum: number): value is string {
  return typeof value === 'string' && value.length > 0 && value.length <= maximum;
}

function isNonNegativeInteger(value: unknown): value is number {
  return Number.isSafeInteger(value) && Number(value) >= 0;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isExactRecord(value: unknown, keys: readonly string[]): value is Record<string, unknown> {
  return (
    isRecord(value) &&
    Object.keys(value).length === keys.length &&
    keys.every((key) => Object.hasOwn(value, key))
  );
}

function isRecordWithOptionalKeys(
  value: unknown,
  required: readonly string[],
  optional: readonly string[],
): value is Record<string, unknown> {
  if (!isRecord(value) || !required.every((key) => Object.hasOwn(value, key))) {
    return false;
  }

  const allowed = new Set([...required, ...optional]);
  return Object.keys(value).every((key) => allowed.has(key));
}
